import * as THREE from 'three/webgpu';

import type { GlbDatumSlotKind, GlbDatumSlotTargetDescriptor } from '../../imported-model/datum-slot';
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata/ImportedModelObjectMetadataService';
import { isMeasureUnboundedObjectId } from '../types/measure-unbounded-types';
import type { MeasureEntityRef } from '../types/measure-types';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import { resolveTopologySelectionPick } from '../../utils/feature';
import type { Viewer } from '../../viewer/Viewer';

/**
 * 测量选中对应的设计树投影 hint 类别。
 */
export type MeasureSelectionTreeHintKind =
    | 'imported-topology'
    | 'native-topology'
    | 'datum-plane'
    | 'datum-axis'
    | 'datum-point'
    | 'glb-datum-slot'
    | 'unresolvable';

/**
 * 导入模型拓扑 hint。
 */
export interface MeasureSelectionImportedTopologyTreeHint {
    /** hint 类别。 */
    kind: 'imported-topology';
    /** 选中对象 ID（可能是拓扑代理）。 */
    selectionObjectId: string;
}

/**
 * 原生 BRep 拓扑 hint。
 */
export interface MeasureSelectionNativeTopologyTreeHint {
    /** hint 类别。 */
    kind: 'native-topology';
    /** 所属实体对象 ID。 */
    bodyObjectId: string;
    /** 所属特征 ID。 */
    owningFeatureId?: string;
}

/**
 * 基准面 hint。
 */
export interface MeasureSelectionDatumPlaneTreeHint {
    /** hint 类别。 */
    kind: 'datum-plane';
    /** 基准面 ID。 */
    planeId: number;
}

/**
 * 基准轴 hint。
 */
export interface MeasureSelectionDatumAxisTreeHint {
    /** hint 类别。 */
    kind: 'datum-axis';
    /** 基准轴 ID。 */
    axisId: number;
}

/**
 * 基准点 hint。
 */
export interface MeasureSelectionDatumPointTreeHint {
    /** hint 类别。 */
    kind: 'datum-point';
    /** 基准点 ID。 */
    pointId: number;
    /** 是否为原点。 */
    isOrigin: boolean;
}

/**
 * GLB datum-slot hint。
 */
export interface MeasureSelectionGlbDatumSlotTreeHint {
    /** hint 类别。 */
    kind: 'glb-datum-slot';
    /** 标签页 ID。 */
    tabId: string;
    /** 宿主对象 ID。 */
    hostObjectId: string;
    /** 槽位类型。 */
    slotKind: GlbDatumSlotKind;
}

/**
 * 无法解析 hint。
 */
export interface MeasureSelectionUnresolvableTreeHint {
    /** hint 类别。 */
    kind: 'unresolvable';
}

/**
 * 测量选中对应的设计树投影 hint。
 */
export type MeasureSelectionTreeHint =
    | MeasureSelectionImportedTopologyTreeHint
    | MeasureSelectionNativeTopologyTreeHint
    | MeasureSelectionDatumPlaneTreeHint
    | MeasureSelectionDatumAxisTreeHint
    | MeasureSelectionDatumPointTreeHint
    | MeasureSelectionGlbDatumSlotTreeHint
    | MeasureSelectionUnresolvableTreeHint;

/**
 * 从测量实体引用构建设计树投影 hint。
 *
 * @param viewer Viewer 实例。
 * @param entityRef 测量实体引用。
 * @returns 设计树投影 hint。
 */
export function buildMeasureSelectionTreeHintFromEntityRef(
    viewer: Viewer,
    entityRef: MeasureEntityRef,
): MeasureSelectionTreeHint {
    const datumSlot = entityRef.unboundedBinding?.datumSlot;
    if (datumSlot) {
        return {
            kind: 'glb-datum-slot',
            tabId: datumSlot.tabId,
            hostObjectId: datumSlot.hostObjectId,
            slotKind: datumSlot.slotKind,
        };
    }

    return buildMeasureSelectionTreeHint(viewer, entityRef.objectId);
}

/**
 * 构建测量选中对象对应的设计树投影 hint。
 *
 * 设计说明：
 * 1. cad-core 只输出与 UI 无关的结构化 hint，treeKey 映射留在 web-client；
 * 2. 优先识别基准与导入拓扑，再回退到原生 BRep 所属实体/特征。
 *
 * @param viewer Viewer 实例。
 * @param objectId 测量选中对象 ID。
 * @returns 设计树投影 hint。
 */
export function buildMeasureSelectionTreeHint(
    viewer: Viewer,
    objectId: string,
): MeasureSelectionTreeHint {
    if (isMeasureUnboundedObjectId(objectId)) {
        return { kind: 'unresolvable' };
    }

    const sceneObject = viewer.findSceneObjectById(objectId);
    if (!sceneObject) {
        return { kind: 'unresolvable' };
    }

    const datumPlaneHint = resolveDatumPlaneTreeHint(viewer, sceneObject);
    if (datumPlaneHint) {
        return datumPlaneHint;
    }

    const datumAxisHint = resolveDatumAxisTreeHint(viewer, sceneObject);
    if (datumAxisHint) {
        return datumAxisHint;
    }

    const datumPointHint = resolveDatumPointTreeHint(viewer, sceneObject);
    if (datumPointHint) {
        return datumPointHint;
    }

    if (ImportedModelObjectMetadataService.resolveSelectionProxyObjectId(sceneObject)?.trim()) {
        return {
            kind: 'imported-topology',
            selectionObjectId: sceneObject.uuid,
        };
    }

    let current: THREE.Object3D | null = sceneObject;
    while (current) {
        if (ImportedModelObjectMetadataService.peekMetadata(current)) {
            return {
                kind: 'imported-topology',
                selectionObjectId: sceneObject.uuid,
            };
        }
        current = current.parent;
    }

    const topologyPick = resolveTopologySelectionPick(sceneObject);
    if (topologyPick) {
        return {
            kind: 'native-topology',
            bodyObjectId: topologyPick.bodyObjectId,
            owningFeatureId: topologyPick.owningFeatureId,
        };
    }

    return { kind: 'unresolvable' };
}

/**
 * 解析基准面对应的设计树 hint。
 *
 * @param viewer Viewer 实例。
 * @param sceneObject 场景对象。
 * @returns 基准面 hint；未命中时返回 null。
 */
function resolveDatumPlaneTreeHint(
    viewer: Viewer,
    sceneObject: THREE.Object3D,
): MeasureSelectionDatumPlaneTreeHint | null {
    if (!isDatumPlaneObject(sceneObject)) {
        return null;
    }

    const planes = viewer.getAllDatumPlanes?.() ?? [];
    const matchedPlane = planes.find((plane) => plane.getObject3D().uuid === sceneObject.uuid);
    if (!matchedPlane) {
        return null;
    }

    return {
        kind: 'datum-plane',
        planeId: matchedPlane.id,
    };
}

/**
 * 解析基准轴对应的设计树 hint。
 *
 * @param viewer Viewer 实例。
 * @param sceneObject 场景对象。
 * @returns 基准轴 hint；未命中时返回 null。
 */
function resolveDatumAxisTreeHint(
    viewer: Viewer,
    sceneObject: THREE.Object3D,
): MeasureSelectionDatumAxisTreeHint | null {
    if (!isDatumAxis(sceneObject)) {
        return null;
    }

    const axes = viewer.datumAxisManager?.getAllAxes?.() ?? [];
    const matchedAxis = axes.find((axis) => axis.getObject3D().uuid === sceneObject.uuid);
    if (!matchedAxis) {
        return null;
    }

    return {
        kind: 'datum-axis',
        axisId: matchedAxis.id,
    };
}

/**
 * 解析基准点对应的设计树 hint。
 *
 * @param viewer Viewer 实例。
 * @param sceneObject 场景对象。
 * @returns 基准点 hint；未命中时返回 null。
 */
function resolveDatumPointTreeHint(
    viewer: Viewer,
    sceneObject: THREE.Object3D,
): MeasureSelectionDatumPointTreeHint | null {
    if (!isDatumPoint(sceneObject)) {
        return null;
    }

    const points = viewer.datumPointManager?.getAllPoints?.() ?? [];
    const matchedPoint = points.find((point) => point.getObject3D().uuid === sceneObject.uuid);
    if (!matchedPoint) {
        return null;
    }

    return {
        kind: 'datum-point',
        pointId: matchedPoint.id,
        isOrigin: matchedPoint.isOrigin,
    };
}
